import { useQuery } from '@tanstack/react-query';
import type {
  ConsultChatThreadResponse,
  HttpRequestFn,
} from './AcsChatApiService';
import { createAcsChatApiService } from './AcsChatApiService';
import type { AcsTokenResponse } from './AcsChatConfig';

// ============================================================================
// useAcsChatApi – React Query hooks for ACS Chat backend calls
// ============================================================================
//
// These hooks wrap the ACS Chat API service with React Query caching.
// Each portal creates its own instances by passing its httpRequest.
//
// Usage:
//
//   // In portal's API/hooks/acsChatHook.ts:
//   import { httpRequest } from '../../shared/lib/utils/httpClient';
//   import { createAcsChatApiHooks } from '@qler-view/shared_lib/src/shared/Chat/useAcsChatApiHook';
//
//   const { useGetAcsChatToken, useGetConsultChatThread } =
//     createAcsChatApiHooks(httpRequest);
//
//   export { useGetAcsChatToken, useGetConsultChatThread };
//
// ============================================================================

export const createAcsChatApiHooks = (httpRequest: HttpRequestFn) => {
  const acsChatApi = createAcsChatApiService(httpRequest);

  /**
   * Fetch ACS chat token for the current user.
   * Call this at login / app init level.
   * The token is cached and won't refetch unless invalidated.
   */
  const useGetAcsChatToken = (
    userId: number,
    portalType: string,
    enabled = true
  ) => {
    const { data, isLoading, isError, error, refetch } =
      useQuery<AcsTokenResponse>({
        queryKey: ['AcsChatToken', userId],
        queryFn: async () => {
          const data = await acsChatApi.getAcsChatToken(userId, portalType);
          return data;
        },
        staleTime: 1000 * 60 * 50, // 50 minutes (ACS tokens expire in ~24h, refresh well before)
        gcTime: 1000 * 60 * 60, // 1 hour
        enabled: !!userId && enabled,
        retry: (failureCount, error) => {
          // Retry up to 2 times for 403 Forbidden errors (token timing issues)
          const err = error as Error & { status?: number };
          if (err.status === 403 && failureCount < 2) {
            return true;
          }
          return false;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
      });

    return {
      acsChatToken: data,
      isLoading,
      isError,
      error,
      refetch,
    };
  };

  /**
   * Fetch the chat thread ID for a specific consult.
   * Call this when the user opens the chat for a consult.
   */
  const useGetConsultChatThread = (
    consultId: number,
    userId: number,
    portalType: string,
    enabled = true
  ) => {
    const { data, isLoading, isError, error, refetch } =
      useQuery<ConsultChatThreadResponse>({
        queryKey: ['ConsultChatThread', consultId],
        queryFn: async () => {
          const data = await acsChatApi.getConsultChatThread(
            consultId,
            userId,
            portalType
          );
          return data;
        },
        staleTime: 0,
        gcTime: 0,
        enabled: !!consultId && enabled,
        retry: (failureCount, error) => {
          // Retry up to 2 times for 403 Forbidden errors (token timing issues)
          const err = error as Error & { status?: number };
          if (err.status === 403 && failureCount < 2) {
            return true;
          }
          return false;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
      });

    return {
      consultChatThread: data,
      isLoading,
      isError,
      error,
      refetch,
    };
  };

  return {
    useGetAcsChatToken,
    useGetConsultChatThread,
  };
};
